import React from "react";
import { Box, Skeleton, Typography } from "@mui/material";
import FilterSelectBox from "./FilterSelectBox";

/** What a dimension shows when the user can reach nothing in it. */
export const NO_ACCESS_DISPLAY_VALUE = "-";

/**
 * Matches the live dropdown's trigger (HierarchyFilterDropdown passes the same
 * value). Without it a long depot name grows the field without bound: the value
 * is `whiteSpace: nowrap`, and the filter row neither wraps nor shrinks, so it
 * would push the page header into horizontal overflow.
 */
const FIELD_MAX_WIDTH = 200;

/**
 * A global filter field with nothing to choose: the single value the user is
 * scoped to, or "-" when they can reach none.
 *
 * It exists because hiding such a field hides information. A user scoped to one
 * business segment has no filtering to do there, but the field is the only place
 * the platform tells them which segment their vehicles are in - and a hidden
 * control reads as a bug, not as an absence of choice. So the three fields are
 * always present and only their interactivity varies.
 *
 * The popover variant reuses FilterSelectBox's disabled mode, which drops the
 * chevron and the click without changing any of the framing, so a read-only
 * field lines up with its live siblings exactly. The accordion variant mirrors
 * the AccordionSummary the dropdown renders, minus the expander.
 */
const ReadOnlyFilterField = ({
  fieldLabel,
  value,
  variant = "popover",
  isLoading = false,
  testId = "readonly-filter",
}) => {
  // While the lists are still being fetched a dimension has zero options for a
  // reason that has nothing to do with access. Showing the no-access dash then
  // would make loading indistinguishable from "you can reach nothing", which is
  // the confusion this component exists to remove - so the field renders a
  // skeleton instead. Blanking the value was the first attempt and was no
  // better: an empty bordered box reads as a broken control rather than a
  // pending one.
  const displayValue = value || NO_ACCESS_DISPLAY_VALUE;

  if (variant === "accordion") {
    return (
      <Box
        data-testid={`${testId}-readonly`}
        aria-busy={isLoading || undefined}
        sx={{
          borderBottom: "1px solid #E6E6E6",
          px: 2,
          py: "18px",
          display: "flex",
          flexDirection: "column",
          gap: 1,
        }}
      >
        <Typography
          variant="fw400s14c000000"
          sx={{ lineHeight: "16px", color: "#1C1C1C" }}
        >
          {fieldLabel}
        </Typography>
        {isLoading ? (
          <Skeleton variant="text" width={120} height={20} />
        ) : (
          <Typography
            variant="fw500s12lh24cA2A2A2"
            sx={{
              color: "#2B2B2B",
              lineHeight: "20px",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {displayValue}
          </Typography>
        )}
      </Box>
    );
  }

  return (
    <FilterSelectBox
      disabled
      loading={isLoading}
      label={fieldLabel}
      displayValue={displayValue}
      maxWidth={FIELD_MAX_WIDTH}
      dataTestId={`${testId}-readonly`}
    />
  );
};

export default ReadOnlyFilterField;
